import React, { useState, useEffect, useCallback } from "react";
import CircularProgressBar from "../components/CircularProgressBar";
import user_image from "../images/user_img.jpg";
import {
  Table,
  Modal,
  Button,
  Dropdown,
  message,
  Input,
  AutoComplete,
  Form,
} from "antd";
import {
  EllipsisOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { listTests, deleteTest } from "../services/tests";
import { getIndustry } from "../services/industries";
import { listFunctions, createFunction } from "../services/functions";
import { listRoles, createRole } from "../services/roles";
import { createSoftware } from "../services/software";
import { Test } from "../types/industry";
import api from "../services/api";
import { toast } from "react-toastify";
import { useUser } from "../contexts/UserContext";
import { useUsageStatus } from "../hooks/useUsageStatus";
import DuplicateAssessmentModal from "../components/DuplicateAssessmentModal";

interface ReqSkillRow {
  id: number;
  skill_name: string;
  created_at?: string;
}

interface RolesRow {
  id: number;
  name: string;
  created_at?: string;
}

interface TestsRow {
  id: number;
  name: string;
  time_limit: number;
  question_count: number;
}

interface CandidatesRow {
  id: number;
  user_email: string;
  user_name: string;
  test_name: string;
  invited_at: string;
  completed_at: string | null;
  score: number | null;
  profile_image?: string;
}

const ViewIndustry: React.FC = () => {
  const { id: industryId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useUser();

  const [loading, setLoading] = useState(false);
  const [activeTabId, setActiveTabId] = useState("tab1");
  const [industry, setIndustry] = useState<{
    id: number;
    name: string;
    color_hex: string;
  } | null>(null);
  const [rows_reqSkill, setrows_reqSkill] = useState<ReqSkillRow[]>([]);
  const [rows_Roles, setrows_Roles] = useState<RolesRow[]>([]);
  const [rows_Tests, setrows_Tests] = useState<TestsRow[]>([]);
  const [rows_Software, setrows_Software] = useState<any[]>([]);
  const [rows_Candidate, setrows_Candidate] = useState<CandidatesRow[]>([]);
  const [delRow, setDelRow] = useState<TestsRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState<{
    open: boolean;
    test: TestsRow | null;
  }>({ open: false, test: null });
  const [emailForm, setEmailForm] = useState({ email: "", body: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [invitationLink, setInvitationLink] = useState<string>("");
  const [duplicateWarning, setDuplicateWarning] = useState<any>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState<any>(null);
  const [confirmRetakeModal, setConfirmRetakeModal] = useState<{
    open: boolean;
    test: TestsRow | null;
    email: string;
    body: string;
  }>({ open: false, test: null, email: "", body: "" });

  // Modal states for creating items
  const [functionModalOpen, setFunctionModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [softwareModalOpen, setSoftwareModalOpen] = useState(false);

  // Form instances and saving states
  const [functionForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [softwareForm] = Form.useForm();
  const [savingFunction, setSavingFunction] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [savingSoftware, setSavingSoftware] = useState(false);

  // Usage status for checking limits
  const { usage, refresh: refreshUsage } = useUsageStatus();

  const columns_reqSkills = [
    { title: "Function Name", dataIndex: "skill_name", key: "skill_name" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    ...(isSuperAdmin() ? [{
      key: "actions",
      width: 60,
      render: (_: unknown, row: ReqSkillRow) => (
        <Dropdown
          menu={{
            items: [{
              key: 'manage',
              label: 'Manage Functions',
              onClick: () => navigate(`/industries/${industryId}/functions`)
            }]
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    }] : []),
  ];


  const columns_Roles = [
    { title: "Role Name", dataIndex: "name", key: "name" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    ...(isSuperAdmin() ? [{
      key: "actions",
      width: 60,
      render: (_: unknown, row: RolesRow) => (
        <Dropdown
          menu={{
            items: [{
              key: 'manage',
              label: 'Manage Roles',
              onClick: () => navigate(`/industries/${industryId}/roles`)
            }]
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    }] : []),
  ];

  const columns_Tests = [
    { title: "Assessment name", dataIndex: "name", key: "name" },
    { title: "Questions", dataIndex: "question_count", key: "question_count" },
    {
      title: "Time limit",
      dataIndex: "time_limit",
      key: "time_limit",
      render: (minutes: number) => `${minutes}:00`,
    },
    {
      key: "actions",
      width: 60,
      render: (_: unknown, row: TestsRow) => {
        const menuItems = [
          {
            key: 'edit-or-view',
            label: isSuperAdmin() ? 'Edit' : 'View questions',
            onClick: () => navigate(`/test-builder/${industryId}/${row.id}`)
          },
          {
            key: 'send',
            label: 'Send to...',
            onClick: () => handleSendTo(row)
          }
        ];

        // Only super admins can delete tests
        if (isSuperAdmin()) {
          menuItems.push({
            key: 'delete',
            label: 'Delete',
            onClick: () => setDelRow(row)
          });
        }

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const columns_Software = [
    { title: "Software name", dataIndex: "name", key: "name" },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    ...(isSuperAdmin() ? [{
      key: "actions",
      width: 60,
      render: (_: unknown, row: any) => (
        <Dropdown
          menu={{
            items: [{
              key: 'manage',
              label: 'Manage Software',
              onClick: () => navigate(`/industries/${industryId}/software`)
            }]
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    }] : []),
  ];

  const columns_Candidates = [
    {
      title: "Applicant name",
      dataIndex: "user_name",
      key: "user_name",
      render: (name: string, row: CandidatesRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flexShrink: 0 }}>
            <img
              src={row.profile_image && row.profile_image.trim() !== '' ? row.profile_image : user_image}
              alt="user"
              className="user_image_candidate"
              onError={(e) => {
                e.currentTarget.src = user_image;
              }}
            />
          </div>
          <div style={{ flex: 1, wordWrap: 'break-word', lineHeight: '1.2' }}>
            {name || "N/A"}
          </div>
        </div>
      ),
    },
    { title: "Email address", dataIndex: "user_email", key: "user_email" },
    { title: "Assessment taken", dataIndex: "test_name", key: "test_name" },
    {
      title: "Assessment taken date/time",
      dataIndex: "invited_at",
      key: "invited_at",
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: "Result",
      dataIndex: "score",
      key: "score",
      render: (score: number | null, row: CandidatesRow) => (
        <>
          {row.completed_at ? (
            <CircularProgressBar
              percentage={score || 0}
              size={60}
              strokeWidth={5}
              progressColor="#0051FF"
            />
          ) : (
            <span>Pending</span>
          )}
        </>
      ),
    },
  ];

  const sampleData_Candidates = [
    {
      applicant_name: "Anna Burnes",
      image: "",
      email: "aburnes@email.com",
      tests_taken: "Advanced Xero Assessment",
      date_time: "01/01/2025 09:25 AM",
      result: 60,
    },
  ];

  /* ─ Delete test ─ */
  /* ─ Handle send to action with usage check ─ */
  const handleSendTo = (test: TestsRow) => {
    // Check usage status before opening modal
    if (usage?.exceeded) {
      Modal.confirm({
        title: 'Subscription Limit Reached',
        content: `You have reached your assessment invitation limit (${usage.current_usage}/${usage.limit}). Please upgrade your plan to send more invitations.`,
        okText: 'Upgrade Plan',
        cancelText: 'Cancel',
        onOk: () => navigate('/subscriptions'),
      });
      return;
    }

    if (usage?.at_risk) {
      toast.warning(`You are approaching your invitation limit (${usage.current_usage}/${usage.limit}). Consider upgrading your plan.`);
    }

    setSendEmailModal({ open: true, test });
  };

  /* ─ Handle test deletion ─ */
  const confirmDelete = async () => {
    if (!delRow) return;
    try {
      setDeleting(true);
      await deleteTest(Number(delRow.id));
      setrows_Tests((prev) => prev.filter((r) => r.id !== delRow.id));
      toast.success("Test deleted successfully");
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
      setDelRow(null);
    }
  };

  /* ─ Send test invitation email ─ */
  const handleSendEmail = async () => {
    if (!emailForm.email.trim()) {
      toast.error("Email address is required");
      return;
    }
    if (!sendEmailModal.test) return;

    // NEW: Check for duplicate assessments
    try {
      const duplicateResponse = await api.post("check-duplicate-assessment/", {
        email: emailForm.email,
        test_id: sendEmailModal.test.id,
      });

      if (duplicateResponse.data.has_duplicate) {
        setDuplicateWarning(duplicateResponse.data);
        setPendingInvitation({
          email: emailForm.email,
          test_id: sendEmailModal.test.id,
          body: emailForm.body,
        });
        setShowDuplicateModal(true);
        return; // Stop here and show modal
      }
    } catch (err) {
      console.error("Error checking duplicates:", err);
      // Continue with invitation if check fails
    }

    // Proceed with normal invitation
    await sendInvitation();
  };

  // New function to handle the actual invitation sending
  const sendInvitation = async () => {
    if (!pendingInvitation && !sendEmailModal.test) return;

    const invitationData = pendingInvitation || {
      email: emailForm.email,
      test_id: sendEmailModal.test?.id || 0,
      body: emailForm.body,
    };

    try {
      setSendingEmail(true);
      const response = await api.post("send-test-invitation/", invitationData);

      toast.success("Test assigned and invitation sent successfully!");

      // Construct the invitation link using the token from backend
      if (response.data && response.data.invitation_token) {
        const invitationUrl = `${window.location.origin}/invitation?token=${response.data.invitation_token}`;
        setInvitationLink(invitationUrl);
      }

      // Clean up
      setShowDuplicateModal(false);
      setDuplicateWarning(null);
      setPendingInvitation(null);

      // Refresh candidates data to show the new assignment
      refreshCandidates();

      // Refresh usage status after successful invitation
      refreshUsage();
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      let errorMessage = "Failed to send invitation. Please try again.";

      if (err.response?.status === 404) {
        errorMessage =
          "User with this email address does not exist. Please check the email or register the user first.";
      } else if (err.response?.status === 400) {
        const responseError = err.response?.data?.error;
        if (responseError?.includes("already completed")) {
          // Show confirmation modal for retake instead of error
          setConfirmRetakeModal({
            open: true,
            test: sendEmailModal.test || { id: invitationData.test_id, name: '', time_limit: 0, question_count: 0 },
            email: invitationData.email,
            body: invitationData.body
          });
          setSendingEmail(false);
          return;
        } else if (responseError?.includes("already assigned")) {
          errorMessage = "This assessment is already assigned to this user.";
        } else {
          errorMessage = responseError || errorMessage;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  /* ─ Handle retake test ─ */
  const handleRetakeTest = async () => {
    if (!confirmRetakeModal.test) return;

    try {
      setSendingEmail(true);
      const response = await api.post("retake-test/", {
        email: confirmRetakeModal.email,
        test_id: confirmRetakeModal.test.id,
        body: confirmRetakeModal.body,
      });

      toast.success("Test retake invitation sent successfully!");

      // Construct the invitation link using the token from backend
      if (response.data && response.data.invitation_token) {
        const invitationUrl = `${window.location.origin}/invitation?token=${response.data.invitation_token}`;
        setInvitationLink(invitationUrl);
      }

      // Close confirmation modal and refresh candidates data
      setConfirmRetakeModal({ open: false, test: null, email: "", body: "" });
      refreshCandidates();
    } catch (err: any) {
      console.error("Failed to send retake invitation:", err);
      toast.error("Failed to send retake invitation. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  /* ─ Handle modal close ─ */
  const handleModalClose = () => {
    setSendEmailModal({ open: false, test: null });
    setEmailForm({ email: "", body: "" });
    setInvitationLink("");
  };

  /* ─ Handle confirmation modal close ─ */
  const handleConfirmRetakeClose = () => {
    setConfirmRetakeModal({ open: false, test: null, email: "", body: "" });
  };

  /* ─ Load user emails for autocomplete ─ */
  const loadUserEmails = async () => {
    try {
      const response = await api.get("users/emails/");
      setUserEmails(response.data);
    } catch (err) {
      console.error("Failed to load user emails:", err);
    }
  };

  useEffect(() => {
    if (!industryId) return;
    (async () => {
      setLoading(true);
      try {
        const [industryData, testsData, candidatesResponse, functionsData, rolesData, softwareData] = await Promise.all(
          [
            getIndustry(Number(industryId)),
            listTests(Number(industryId)),
            api.get(`assignments/?industry=${industryId}`, { timeout: 10000 }),
            listFunctions(Number(industryId)),
            listRoles(Number(industryId)),
            api.get(`software/?industry=${industryId}`),
          ]
        );

        setIndustry(industryData);
        setrows_Tests(
          testsData.map((t: Test) => ({
            id: t.id!,
            name: t.name,
            time_limit: t.time_limit,
            question_count: t.question_count ?? 0,
          }))
        );
        setrows_reqSkill(
          functionsData.map((func) => ({
            id: func.id,
            skill_name: func.name,
            created_at: func.created_at,
          }))
        );

        // Set roles data
        setrows_Roles(
          rolesData.map((role) => ({
            id: role.id,
            name: role.name,
            created_at: role.created_at,
          }))
        );

        // Set software data
        setrows_Software(
          softwareData.data.map((s: any) => ({
            id: s.id,
            name: s.name,
            created_at: s.created_at,
          }))
        );

        // Ensure candidatesData is always an array
        const candidatesData = Array.isArray(candidatesResponse.data)
          ? candidatesResponse.data
          : [];
        console.log("Initial candidates data loaded:", candidatesData.length, "candidates"); // Debug log
        setrows_Candidate(candidatesData);
      } catch (error: any) {
        console.error("Error loading data:", error);

        // Handle different error scenarios with user-friendly messages
        if (error.response?.status === 404) {
          toast.error("Industry not found. Redirecting to industries list...");
          setTimeout(() => {
            navigate('/industries-list');
          }, 2000);
        } else if (error.response?.status === 403) {
          toast.error("You do not have permission to access this industry.");
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          toast.error("Network error. Please check your connection and try again.");
        } else {
          toast.error("Failed to load industry data. Please try again later.");
        }

        // Set empty arrays on error
        setrows_Tests([]);
        setrows_Candidate([]);
        setrows_reqSkill([]);
        setrows_Roles([]);
        setrows_Software([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [industryId]);

  // Function to refresh candidates data
  const refreshCandidates = useCallback(async () => {
    if (!industryId) return;
    try {
      const candidatesResponse = await api.get(
        `assignments/?industry=${industryId}`
      );
      const candidatesData = Array.isArray(candidatesResponse.data)
        ? candidatesResponse.data
        : [];
      console.log('Refreshed candidates data:', candidatesData);
      setrows_Candidate(candidatesData);
    } catch (error) {
      console.error("Error refreshing candidates:", error);
      // Don't clear data on refresh error, keep existing data
    }
  }, [industryId]);

  // Listen for window focus to refresh when returning from register page
  useEffect(() => {
    window.addEventListener("focus", refreshCandidates);
    return () => {
      window.removeEventListener("focus", refreshCandidates);
    };
  }, [refreshCandidates]);

  // Load user emails for autocomplete
  useEffect(() => {
    loadUserEmails();
  }, []);



  // Dynamic button configuration based on active tab
  const getDynamicButtonConfig = () => {
    switch (activeTabId) {
      case "tab1": // Assessments
        return {
          text: "Create new assessment",
          action: () => navigate(`/test-builder/${industryId}`)
        };
      case "tab2": // Functions
        return {
          text: "Create new function",
          action: () => setFunctionModalOpen(true)
        };
      case "tab3": // Roles
        return {
          text: "Create new role",
          action: () => setRoleModalOpen(true)
        };
      case "tab4": // Software
        return {
          text: "Create new software",
          action: () => setSoftwareModalOpen(true)
        };
      case "tab5": // Candidates
        return {
          text: "Invite new candidate",
          action: () => navigate(`/candidate/register?industry=${industryId}`)
        };
      default:
        return {
          text: "Create new assessment",
          action: () => navigate(`/test-builder/${industryId}`)
        };
    }
  };

  // Save functions for creating new items
  const handleSaveFunction = async (values: { name: string }) => {
    if (!industryId) return;
    setSavingFunction(true);
    try {
      await createFunction({ name: values.name, industry: Number(industryId) });
      message.success("Function created successfully");
      setFunctionModalOpen(false);
      functionForm.resetFields();
      // Refresh functions data
      const functionsData = await listFunctions(Number(industryId));
      setrows_reqSkill(
        functionsData.map((func) => ({
          id: func.id,
          skill_name: func.name,
          created_at: func.created_at,
        }))
      );
    } catch (error) {
      console.error("Error creating function:", error);
      message.error("Failed to create function");
    } finally {
      setSavingFunction(false);
    }
  };

  const handleSaveRole = async (values: { name: string }) => {
    if (!industryId) return;
    setSavingRole(true);
    try {
      await createRole({ name: values.name, industry: Number(industryId) });
      message.success("Role created successfully");
      setRoleModalOpen(false);
      roleForm.resetFields();
      // Refresh roles data
      const rolesData = await listRoles(Number(industryId));
      setrows_Roles(
        rolesData.map((role) => ({
          id: role.id,
          name: role.name,
          created_at: role.created_at,
        }))
      );
    } catch (error) {
      console.error("Error creating role:", error);
      message.error("Failed to create role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleSaveSoftware = async (values: { name: string }) => {
    if (!industryId) return;
    setSavingSoftware(true);
    try {
      await createSoftware({ name: values.name, industry: Number(industryId) });
      message.success("Software created successfully");
      setSoftwareModalOpen(false);
      softwareForm.resetFields();
      // Refresh software data
      const softwareResponse = await api.get(`software/?industry=${industryId}`);
      setrows_Software(
        softwareResponse.data.map((s: any) => ({
          id: s.id,
          name: s.name,
          created_at: s.created_at,
        }))
      );
    } catch (error) {
      console.error("Error creating software:", error);
      message.error("Failed to create software");
    } finally {
      setSavingSoftware(false);
    }
  };

  const AssessmentTabs = [
    {
      id: "tab1",
      label: "Assessments",
      content: (
        <>
          <div className="card mb-3">
            <div className="card-body sectionMinHeight">
              <div className="row mb-3">
                <div className="col">
                  <Table
                    className="table"
                    columns={columns_Tests}
                    dataSource={rows_Tests}
                    rowKey="id"
                    pagination={false}
                    bordered
                    loading={loading}
                    scroll={{ y: 700 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: "tab2",
      label: "Functions",
      content: (
        <div className="card mb-3">
          <div className="card-body sectionMinHeight">
            <Table
              className="table"
              columns={columns_reqSkills}
              dataSource={rows_reqSkill}
              rowKey="id"
              pagination={false}
              bordered
              loading={loading}
              scroll={{ y: 700 }}
            />
          </div>
        </div>
      ),
    },
    {
      id: "tab3",
      label: "Roles",
      content: (
        <div className="card mb-3">
          <div className="card-body sectionMinHeight">
            <Table
              className="table"
              columns={columns_Roles}
              dataSource={rows_Roles}
              rowKey="id"
              pagination={false}
              bordered
              loading={loading}
              scroll={{ y: 700 }}
            />
          </div>
        </div>
      ),
    },
    {
      id: "tab4",
      label: "Software",
      content: (
        <div className="card mb-3">
          <div className="card-body sectionMinHeight">
            <Table
              className="table"
              columns={columns_Software}
              dataSource={rows_Software}
              rowKey="id"
              pagination={false}
              bordered
              loading={loading}
              scroll={{ y: 700 }}
            />
          </div>
        </div>
      ),
    },
    {
      id: "tab5",
      label: "Candidates",
      content: (
        <>
          <div className="card mb-3">
            <div className="card-body sectionMinHeight">
              <div className="row mb-3">
                <div className="col">
                  <Table
                    className="table"
                    columns={columns_Candidates}
                    dataSource={rows_Candidate}
                    rowKey="id"
                    pagination={false}
                    bordered
                    loading={loading}
                    scroll={{ y: 700 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="content-header">
        <div className="row">
          <div className="col">
            <Button
              type="text"
              onClick={() => navigate("/industries-list")}
              style={{
                padding: "4px 0",
                marginBottom: "4px",
                fontSize: "14px",
                color: "#666"
              }}
            >
              ← Back
            </Button>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <h3>
              <strong>{industry?.name || "Loading..."}</strong>
            </h3>
          </div>
        </div>
      </div>
      <div className="section-content layout" style={{ marginTop: 0 }}>
        <div className="mb-3">
          <style>
            {`
              .nav-tabs .nav-item .nav-link {
                font-weight: 400 !important;
                font-style: normal !important;
                font-size: 14px !important;
                min-width: 8.125rem !important;
              }
              .nav-tabs .nav-item .nav-link.active,
              .nav-tabs .nav-item .nav-link:hover,
              .nav-tabs .nav-item .nav-link:focus {
                font-weight: 400 !important;
                border-bottom-width: 2px !important;
                border-bottom-color: #000000ff !important;
              }
              .nav-tabs {
                border-bottom: 1px solid #dee2e6 !important;
              }
              .tabs-header-row {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-end !important;
              }
            `}
          </style>
          <div className="tabs-header-row">
            <ul className="nav nav-tabs" style={{ marginBottom: 0, flexGrow: 1 }}>
              {AssessmentTabs.map((tab) => (
                <li className="nav-item" role="presentation" key={tab.id}>
                  <button
                    className={`nav-link ${tab.id === activeTabId ? 'active' : ''}`}
                    onClick={() => setActiveTabId(tab.id)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
            {isSuperAdmin() && getDynamicButtonConfig() && (
              <div style={{ marginLeft: "20px", marginRight: "16px" }}>
                <Button
                  onClick={getDynamicButtonConfig()?.action}
                  style={{
                    borderRadius: 9999,
                    padding: "10px 40px",
                    backgroundColor: "#1D1F48",
                    color: 'white',
                    height: "auto",
                    marginBottom: "16px",
                    border: 'none'
                  }}
                >
                  {getDynamicButtonConfig()?.text}
                </Button>
              </div>
            )}
          </div>
          <div>
            {AssessmentTabs.find((tab) => tab.id === activeTabId)?.content}
          </div>
        </div>
      </div>

      <Modal
        open={!!delRow}
        title="Delete assessment?"
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        onOk={confirmDelete}
        onCancel={() => setDelRow(null)}
      >
        {`Are you sure you want to delete "${delRow?.name}"?`}
      </Modal>

      <Modal
        open={sendEmailModal.open}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            {invitationLink ? "Close" : "Cancel"}
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sendingEmail}
            onClick={handleSendEmail}
            disabled={!!invitationLink}
            style={{
              backgroundColor: "#2f3349",
              borderColor: "#2f3349",
            }}
          >
            Send
          </Button>,
        ]}
        onCancel={handleModalClose}
        centered
        width={500}
      >
        <div style={{ padding: "20px 0" }}>
          <h3 style={{ textAlign: "center" }}>{sendEmailModal.test?.name}</h3>
          <div style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
            Send to...
          </div>

          <div style={{ marginBottom: 16 }}>
            <AutoComplete
              style={{ width: "100%" }}
              placeholder="Email address"
              value={emailForm.email}
              onChange={(value) =>
                setEmailForm((prev) => ({ ...prev, email: value }))
              }
              options={userEmails.map((email) => ({
                value: email,
                label: email,
              }))}
              filterOption={(inputValue, option) =>
                option?.value
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase()) || false
              }
              disabled={!!invitationLink}
            />
          </div>
          {/* 
          <div style={{ marginBottom: invitationLink ? 20 : 0 }}>
            <Input.TextArea
              placeholder="Body text"
              value={emailForm.body}
              onChange={(e) =>
                setEmailForm((prev) => ({ ...prev, body: e.target.value }))
              }
              rows={4}
              disabled={!!invitationLink}
              style={{
                backgroundColor: "#f0f0f0",
                border: "none",
                borderRadius: 8,
                resize: "none",
              }}
            />
          </div> */}

          {invitationLink && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f6f8fa",
                border: "1px solid #d0d7de",
                borderRadius: 8,
                marginTop: 16,
              }}
            >
              <div
                style={{ marginBottom: 8, fontWeight: 600, color: "#24292f" }}
              >
                Invitation Link:
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d0d7de",
                  borderRadius: 6,
                  fontSize: "14px",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigator.clipboard.writeText(invitationLink);
                  toast.success("Link copied to clipboard!");
                }}
              >
                {invitationLink}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: "12px",
                  color: "#656d76",
                  textAlign: "center",
                }}
              >
                Click to copy link to clipboard
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Confirmation Modal for Retake */}
      <Modal
        open={confirmRetakeModal.open}
        title="Candidate has already completed this assessment"
        onCancel={handleConfirmRetakeClose}
        footer={[
          <Button key="cancel" onClick={handleConfirmRetakeClose}>
            Cancel
          </Button>,
          <Button
            key="retake"
            type="primary"
            loading={sendingEmail}
            onClick={handleRetakeTest}
            style={{
              backgroundColor: "#2f3349",
              borderColor: "#2f3349",
            }}
          >
            Yes, Reassess
          </Button>,
        ]}
        centered
        width={450}
      >
        <div style={{ padding: "20px 0" }}>
          <p>
            The candidate <strong>{confirmRetakeModal.email}</strong> has already completed
            the assessment <strong>"{confirmRetakeModal.test?.name}"</strong>.
          </p>
          <p>
            Do you want to reassess this candidate?
          </p>
        </div>
      </Modal>

      {/* Duplicate Assessment Warning Modal */}
      <DuplicateAssessmentModal
        visible={showDuplicateModal}
        onCancel={() => {
          setShowDuplicateModal(false);
          setDuplicateWarning(null);
          setPendingInvitation(null);
        }}
        onProceed={sendInvitation}
        warning={duplicateWarning}
        loading={sendingEmail}
      />

      {/* Create Function Modal */}
      <Modal
        open={functionModalOpen}
        title="Add New Function"
        okText="Save"
        okButtonProps={{ loading: savingFunction }}
        cancelButtonProps={{ disabled: savingFunction }}
        onOk={() => functionForm.submit()}
        onCancel={() => {
          setFunctionModalOpen(false);
          functionForm.resetFields();
        }}
      >
        <Form
          form={functionForm}
          layout="vertical"
          onFinish={handleSaveFunction}
        >
          <Form.Item
            name="name"
            label="Function Name"
            rules={[{ required: true, message: "Please enter function name" }]}
          >
            <Input placeholder="Enter function name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Role Modal */}
      <Modal
        open={roleModalOpen}
        title="Add New Role"
        okText="Save"
        okButtonProps={{ loading: savingRole }}
        cancelButtonProps={{ disabled: savingRole }}
        onOk={() => roleForm.submit()}
        onCancel={() => {
          setRoleModalOpen(false);
          roleForm.resetFields();
        }}
      >
        <Form
          form={roleForm}
          layout="vertical"
          onFinish={handleSaveRole}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: "Please enter role name" }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Software Modal */}
      <Modal
        open={softwareModalOpen}
        title="Add New Software"
        okText="Save"
        okButtonProps={{ loading: savingSoftware }}
        cancelButtonProps={{ disabled: savingSoftware }}
        onOk={() => softwareForm.submit()}
        onCancel={() => {
          setSoftwareModalOpen(false);
          softwareForm.resetFields();
        }}
      >
        <Form
          form={softwareForm}
          layout="vertical"
          onFinish={handleSaveSoftware}
        >
          <Form.Item
            name="name"
            label="Software Name"
            rules={[{ required: true, message: "Please enter software name" }]}
          >
            <Input placeholder="Enter software name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ViewIndustry;
